import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * The card every screen under `Pages/Auth` was built from: a centred column on
 * the `_AuthLayout` gradient, holding a shadowed card with a logo, a heading and
 * whatever the screen puts inside.
 *
 * The eight Razor pages repeated this block verbatim and drifted in the places
 * you would expect — two of them stop at `col-xl-5` instead of `col-xxl-4`, and
 * those same two swap the body padding for `p-5 text-center`. Both variants are
 * kept, because reproducing the current design is the point; `centered` selects
 * the pair.
 */
interface AuthCardProps {
  title?: string;
  subtitle?: ReactNode;
  /** Sits between the logo and the title. The mandatory password change puts its warning here. */
  aboveTitle?: ReactNode;
  showLogo?: boolean;
  /** The AccessDenied/Lockout shape: one breakpoint wider, centred body, no form. */
  centered?: boolean;
  /** Rendered under the card, still inside the column. */
  footer?: ReactNode;
  children?: ReactNode;
}

export function AuthCard({
  title,
  subtitle,
  aboveTitle,
  showLogo = true,
  centered = false,
  footer,
  children,
}: AuthCardProps) {
  const hasHeader = showLogo || aboveTitle != null || title != null;

  return (
    <div className="row justify-content-center align-items-center min-vh-100">
      <div className={`col-12 col-sm-10 col-md-8 col-lg-6 col-xl-5${centered ? '' : ' col-xxl-4'}`}>
        <div className="card shadow-lg border-0">
          <div className={`card-body ${centered ? 'p-5 text-center' : 'p-4 p-md-5'}`}>
            {hasHeader && (
              <div className="text-center mb-4">
                {showLogo && (
                  <img src="/myui/img/logo.png" alt="Logo" className="mb-3" style={{ height: 60 }} />
                )}
                {aboveTitle}
                {title != null && <h2 className="fw-bold mb-2">{title}</h2>}
                {subtitle != null && <p className="text-muted">{subtitle}</p>}
              </div>
            )}

            {children}
          </div>
        </div>

        {footer}
      </div>
    </div>
  );
}

/**
 * The Privacy/Terms line under the card. Plain anchors: both targets are MVC
 * routes outside the SPA, so following one is a document navigation.
 */
export function AuthCardLinks() {
  const { t } = useTranslation();

  return (
    <div className="text-center mt-4">
      <p className="text-muted small">
        <a href="/Home/Privacy" className="text-decoration-none me-3">
          {t('PrivacyPolicy', 'Privacy Policy')}
        </a>
        <a href="/Home/Terms" className="text-decoration-none">
          {t('TermsOfService', 'Terms of Service')}
        </a>
      </p>
    </div>
  );
}

/**
 * The `asp-validation-summary` alert: errors the server did not attach to a
 * particular input. `AccountService` reports almost everything this way — its
 * `AccountError.Field` is empty for every failure except model binding — so this
 * is where a wrong current password or a spent reset token actually surfaces.
 */
export function AuthErrorSummary({ errors }: { errors: string[] }) {
  if (errors.length === 0) return null;

  return (
    <div className="alert alert-danger" role="alert">
      {errors.length === 1 ? (
        errors[0]
      ) : (
        <ul className="mb-0 ps-3">
          {errors.map((message) => (
            <li key={message}>{message}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
